import React, { useEffect, useMemo, useState } from "react";
import {
  Modal, Tabs, Tag, Button, Form, Select, DatePicker, InputNumber, Input,
  Popconfirm, List, Upload, Space, Empty, Spin, Alert, Divider,
} from "antd";
import { UploadOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getCita, updateCita, cancelarCita, reagendarCita,
  upsertAutorizacionMenor, upsertCirugia,
  listParticipantes, addParticipante, removeParticipante,
  listMedios, addMedio, deleteMedio,
  listConsentimientos, createConsentimiento, firmarConsentimiento,
  listEspacios, listProcedimientosQuirurgicos, listTiposConsentimiento,
  getPaciente,
} from "../../api/agendaApi";
import {
  TIPO_CITA_OPTIONS, TIPO_CITA_LABELS, ESTADO_LABELS, ESTADO_COLORS, esMenorDeEdad,
} from "./agendaHelpers";
import useDoctorOptions from "./useDoctorOptions";
import { toast } from "../toasts/toastStore";
import CitaFormModal from "./CitaFormModal";
import { PATH } from "../../redux/utils";
import { usePermisos } from "../../context/PermisosContext";

const { TextArea } = Input;

export default function CitaDetalleModal({ open, idCita, onClose, onChanged, onNavigateCita }) {
  const [cita, setCita] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = () => {
    if (!idCita) return;
    setCargando(true);
    getCita(idCita)
      .then(setCita)
      .catch(() => toast.error("No se pudo cargar la cita"))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    if (open && idCita) cargar();
    if (!open) setCita(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idCita]);

  const refrescar = (mensaje) => {
    if (mensaje) toast.success(mensaje);
    cargar();
    onChanged && onChanged();
  };

  const estado = cita?.estado;
  const puedeEditar = estado && !["cancelada", "reagendada"].includes(estado);

  return (
    <Modal
      title={cita ? `Cita #${cita.id_cita} — ${cita.paciente_nombre || "Paciente"}` : "Cita"}
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {cargando && !cita && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      )}

      {cita && (
        <>
          <div className="agc-detalle-header">
            <Tag color={ESTADO_COLORS[cita.estado] || "default"}>{ESTADO_LABELS[cita.estado] || cita.estado}</Tag>
            <Tag>{TIPO_CITA_LABELS[cita.tipo_cita] || cita.tipo_cita}</Tag>
            <span className="agc-detalle-fecha">
              {dayjs(cita.fecha_hora_inicio).format("dddd D [de] MMMM [de] YYYY, HH:mm")}
            </span>
          </div>

          {cita.seguimiento_origen && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <span>
                  Nace de la cita{" "}
                  <a onClick={() => onNavigateCita && onNavigateCita(cita.seguimiento_origen.id_cita_origen)}>
                    #{cita.seguimiento_origen.id_cita_origen}
                  </a>{" "}
                  ({dayjs(cita.seguimiento_origen.origen_fecha_hora_inicio).format("D MMM YYYY HH:mm")}, estado{" "}
                  {ESTADO_LABELS[cita.seguimiento_origen.origen_estado] || cita.seguimiento_origen.origen_estado})
                </span>
              }
            />
          )}

          {Array.isArray(cita.derivadas) && cita.derivadas.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <span>
                  Citas derivadas de esta:{" "}
                  {cita.derivadas.map((d) => (
                    <a key={d.id_cita} onClick={() => onNavigateCita && onNavigateCita(d.id_cita)} style={{ marginRight: 8 }}>
                      #{d.id_cita}
                    </a>
                  ))}
                </span>
              }
            />
          )}

          <div className="agc-detalle-actions">
            <AccionCancelar cita={cita} onDone={() => refrescar("Cita cancelada")} />
            <AccionReagendar cita={cita} onDone={(nuevoId) => {
              refrescar("Cita reagendada");
              if (nuevoId && onNavigateCita) onNavigateCita(nuevoId);
            }} />
            <AccionSeguimiento cita={cita} onDone={() => refrescar()} />
          </div>

          <Tabs
            defaultActiveKey="general"
            items={[
              {
                key: "general",
                label: "General",
                children: <TabGeneral cita={cita} puedeEditar={puedeEditar} onSaved={() => refrescar("Cita actualizada")} />,
              },
              cita.tipo_cita === "cirugia" && {
                key: "cirugia",
                label: "Cirugía",
                children: <TabCirugia cita={cita} onSaved={() => refrescar("Cirugía actualizada")} />,
              },
              {
                key: "autorizacion",
                label: "Autorización menor",
                children: <TabAutorizacionMenor cita={cita} onSaved={() => refrescar("Autorización guardada")} />,
              },
              {
                key: "participantes",
                label: "Participantes",
                children: <TabParticipantes cita={cita} onChanged={refrescar} />,
              },
              {
                key: "medios",
                label: "Medios",
                children: <TabMedios cita={cita} onChanged={refrescar} />,
              },
              {
                key: "consentimientos",
                label: "Consentimientos",
                children: <TabConsentimientos cita={cita} onChanged={refrescar} />,
              },
            ].filter(Boolean)}
          />
        </>
      )}
    </Modal>
  );
}

// ---------------- acciones ----------------

function AccionCancelar({ cita, onDone }) {
  const { perm } = usePermisos() || { perm: () => true };
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);

  if (cita.estado === "cancelada" || cita.estado === "reagendada") return null;

  const confirmar = async () => {
    if (!motivo.trim()) {
      toast.error("Escribe el motivo de la cancelación");
      return;
    }
    setLoading(true);
    try {
      await cancelarCita(cita.id_cita, motivo.trim());
      setOpen(false);
      setMotivo("");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cancelar la cita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {perm("agenda", "editar") && (
        <Button danger onClick={() => setOpen(true)}>Cancelar cita</Button>
      )}
      <Modal
        title="Cancelar cita"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={confirmar}
        confirmLoading={loading}
        okText="Confirmar cancelación"
        okButtonProps={{ danger: true }}
        cancelText="Cerrar"
      >
        <p>Motivo de la cancelación (obligatorio):</p>
        <TextArea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </Modal>
    </>
  );
}

function AccionReagendar({ cita, onDone }) {
  const { perm } = usePermisos() || { perm: () => true };
  const [open, setOpen] = useState(false);
  const [fecha, setFecha] = useState(null);
  const [duracion, setDuracion] = useState(cita.duracion_minutos);
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);

  if (cita.estado === "cancelada" || cita.estado === "reagendada") return null;

  const confirmar = async () => {
    if (!fecha) {
      toast.error("Selecciona la nueva fecha y hora");
      return;
    }
    setLoading(true);
    try {
      const res = await reagendarCita(cita.id_cita, {
        fecha_hora_inicio: fecha.format("YYYY-MM-DD HH:mm:ss"),
        duracion_minutos: duracion || undefined,
        motivo: motivo || undefined,
      });
      setOpen(false);
      onDone(res?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reagendar la cita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {perm("agenda", "editar") && (
        <Button onClick={() => setOpen(true)}>Reagendar</Button>
      )}
      <Modal
        title="Reagendar cita"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={confirmar}
        confirmLoading={loading}
        okText="Reagendar"
        cancelText="Cerrar"
      >
        <p>La cita actual quedará marcada como "reagendada" y se creará una cita nueva.</p>
        <Form layout="vertical">
          <Form.Item label="Nueva fecha y hora" required>
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} value={fecha} onChange={setFecha} />
          </Form.Item>
          <Form.Item label="Duración (min)">
            <InputNumber style={{ width: "100%" }} min={5} step={5} value={duracion} onChange={setDuracion} />
          </Form.Item>
          <Form.Item label="Motivo del reagendo">
            <TextArea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function AccionSeguimiento({ cita, onDone }) {
  const { perm } = usePermisos() || { perm: () => true };
  const [open, setOpen] = useState(false);
  return (
    <>
      {perm("agenda", "insertar") && (
        <Button icon={<PlusOutlined />} onClick={() => setOpen(true)}>Crear seguimiento</Button>
      )}
      <CitaFormModal
        open={open}
        onClose={() => setOpen(false)}
        seguimientoDeIdCita={cita.id_cita}
        valoresIniciales={{
          id_paciente: cita.id_paciente,
          id_espacio: cita.id_espacio,
          id_user_doctor: cita.id_user_doctor,
          tipo_cita: "seguimiento",
          origen: "interno",
          duracion_minutos: cita.duracion_minutos,
          fecha_hora_inicio: dayjs().add(1, "day").hour(9).minute(0),
        }}
        onCreated={() => {
          setOpen(false);
          onDone();
        }}
      />
    </>
  );
}

// ---------------- tab: general ----------------

function TabGeneral({ cita, puedeEditar, onSaved }) {
  const [form] = Form.useForm();
  const [espacios, setEspacios] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const { doctores, disponible } = useDoctorOptions();

  useEffect(() => {
    listEspacios().then(setEspacios).catch(() => setEspacios([]));
    form.setFieldsValue({
      id_espacio: cita.id_espacio,
      id_user_doctor: cita.id_user_doctor,
      tipo_cita: cita.tipo_cita,
      fecha_hora_inicio: dayjs(cita.fecha_hora_inicio),
      duracion_minutos: cita.duracion_minutos,
      motivo: cita.motivo,
      estado: ["confirmada", "completada", "no_show"].includes(cita.estado) ? cita.estado : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cita]);

  const guardar = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);
      await updateCita(cita.id_cita, {
        id_espacio: values.id_espacio || null,
        id_user_doctor: values.id_user_doctor || null,
        tipo_cita: values.tipo_cita,
        fecha_hora_inicio: values.fecha_hora_inicio.format("YYYY-MM-DD HH:mm:ss"),
        duracion_minutos: values.duracion_minutos,
        motivo: values.motivo || null,
        estado: values.estado || undefined,
      });
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(err?.response?.data?.detail || "No se pudo actualizar la cita");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Form form={form} layout="vertical" disabled={!puedeEditar} requiredMark={false}>
      {!puedeEditar && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Esta cita ya no se puede editar (está cancelada o fue reagendada)."
        />
      )}
      <div style={{ display: "flex", gap: 16 }}>
        <Form.Item name="tipo_cita" label="Tipo de cita" style={{ flex: 1 }} rules={[{ required: true }]}>
          <Select options={TIPO_CITA_OPTIONS} />
        </Form.Item>
        <Form.Item name="estado" label="Estado" style={{ flex: 1 }}>
          <Select
            allowClear
            placeholder="Sin cambio"
            options={[
              { value: "confirmada", label: "Confirmada" },
              { value: "completada", label: "Completada" },
              { value: "no_show", label: "No se presentó" },
            ]}
          />
        </Form.Item>
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <Form.Item name="fecha_hora_inicio" label="Fecha y hora" style={{ flex: 1 }} rules={[{ required: true }]}>
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="duracion_minutos" label="Duración (min)" style={{ width: 160 }} rules={[{ required: true }]}>
          <InputNumber min={5} step={5} style={{ width: "100%" }} />
        </Form.Item>
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <Form.Item name="id_espacio" label="Espacio" style={{ flex: 1 }}>
          <Select allowClear options={espacios.map((e) => ({ value: e.id_espacio, label: e.nombre }))} />
        </Form.Item>
        <Form.Item name="id_user_doctor" label="Doctor" style={{ flex: 1 }}>
          {disponible ? (
            <Select allowClear showSearch optionFilterProp="label" options={doctores.map((d) => ({ value: d.id_user, label: d.nombre }))} />
          ) : (
            <InputNumber style={{ width: "100%" }} min={1} />
          )}
        </Form.Item>
      </div>
      <Form.Item name="motivo" label="Motivo">
        <TextArea rows={3} />
      </Form.Item>
      {puedeEditar && (
        <Button type="primary" loading={guardando} onClick={guardar}>
          Guardar cambios
        </Button>
      )}
    </Form>
  );
}

// ---------------- tab: cirugia ----------------

function TabCirugia({ cita, onSaved }) {
  const [form] = Form.useForm();
  const [procedimientos, setProcedimientos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    listProcedimientosQuirurgicos().then(setProcedimientos).catch(() => setProcedimientos([]));
    form.setFieldsValue({
      id_procedimiento: cita.cirugia?.id_procedimiento,
      notas: cita.cirugia?.notas,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cita]);

  const guardar = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);
      await upsertCirugia(cita.id_cita, { id_procedimiento: values.id_procedimiento, notas: values.notas || null });
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(err?.response?.data?.detail || "No se pudo guardar la cirugía");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Form form={form} layout="vertical" requiredMark={false}>
      <Form.Item name="id_procedimiento" label="Procedimiento quirúrgico" rules={[{ required: true }]}>
        <Select options={procedimientos.map((p) => ({ value: p.id_procedimiento, label: p.nombre }))} />
      </Form.Item>
      <Form.Item name="notas" label="Notas">
        <TextArea rows={3} />
      </Form.Item>
      <Button type="primary" loading={guardando} onClick={guardar}>Guardar</Button>
    </Form>
  );
}

// ---------------- tab: autorizacion menor ----------------

function TabAutorizacionMenor({ cita, onSaved }) {
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const [pacienteInfo, setPacienteInfo] = useState(null);

  useEffect(() => {
    getPaciente(cita.id_paciente)
      .then(setPacienteInfo)
      .catch(() => setPacienteInfo(null));
    form.setFieldsValue({
      nombre_autoriza: cita.autorizacion_menor?.nombre_autoriza,
      parentesco: cita.autorizacion_menor?.parentesco,
      telefono: cita.autorizacion_menor?.telefono,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cita]);

  const menor = esMenorDeEdad(pacienteInfo?.fecha_nacimiento);

  const guardar = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);
      await upsertAutorizacionMenor(cita.id_cita, values);
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(err?.response?.data?.detail || "No se pudo guardar la autorización");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      {!cita.autorizacion_menor && !menor && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="No hay indicio de que el paciente sea menor de edad, pero puedes registrar la autorización igual si aplica."
        />
      )}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="nombre_autoriza" label="Nombre de quien autoriza" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <div style={{ display: "flex", gap: 16 }}>
          <Form.Item name="parentesco" label="Parentesco" style={{ flex: 1 }} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="telefono" label="Teléfono" style={{ flex: 1 }}>
            <Input />
          </Form.Item>
        </div>
        <Button type="primary" loading={guardando} onClick={guardar}>Guardar autorización</Button>
      </Form>
    </>
  );
}

// ---------------- tab: participantes ----------------

function TabParticipantes({ cita, onChanged }) {
  const [participantes, setParticipantes] = useState(cita.participantes || []);
  const [idUser, setIdUser] = useState(null);
  const [rol, setRol] = useState("");
  const [loading, setLoading] = useState(false);
  const { doctores, disponible } = useDoctorOptions();

  useEffect(() => {
    setParticipantes(cita.participantes || []);
  }, [cita]);

  const agregar = async () => {
    if (!idUser) {
      toast.error("Indica el usuario a agregar");
      return;
    }
    setLoading(true);
    try {
      const res = await addParticipante(cita.id_cita, { id_user: idUser, rol_en_cita: rol || undefined });
      setParticipantes(res.participantes || []);
      setIdUser(null);
      setRol("");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar el participante");
    } finally {
      setLoading(false);
    }
  };

  const quitar = async (idCitaParticipante) => {
    try {
      const res = await removeParticipante(cita.id_cita, idCitaParticipante);
      setParticipantes(res.participantes || []);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo quitar el participante");
    }
  };

  return (
    <div>
      <List
        dataSource={participantes}
        locale={{ emptyText: <Empty description="Sin participantes" /> }}
        renderItem={(p) => (
          <List.Item
            actions={[
              <Popconfirm title="¿Quitar participante?" onConfirm={() => quitar(p.id_cita_participante)}>
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>,
            ]}
          >
            <span>{p.user_nombre}{p.rol_en_cita ? ` — ${p.rol_en_cita}` : ""}</span>
          </List.Item>
        )}
      />
      <Divider />
      <Space wrap>
        {disponible ? (
          <Select
            style={{ width: 220 }}
            showSearch
            optionFilterProp="label"
            placeholder="Selecciona usuario"
            value={idUser}
            onChange={setIdUser}
            options={doctores.map((d) => ({ value: d.id_user, label: d.nombre }))}
          />
        ) : (
          <InputNumber placeholder="ID de usuario" min={1} value={idUser} onChange={setIdUser} />
        )}
        <Input placeholder="Rol en la cita (opcional)" style={{ width: 200 }} value={rol} onChange={(e) => setRol(e.target.value)} />
        <Button type="primary" icon={<PlusOutlined />} loading={loading} onClick={agregar}>Agregar</Button>
      </Space>
    </div>
  );
}

// ---------------- tab: medios ----------------

function TabMedios({ cita, onChanged }) {
  const [medios, setMedios] = useState(cita.medios || []);
  const [tipo, setTipo] = useState("foto");
  const [nota, setNota] = useState("");
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => {
    listMedios(cita.id_cita).then(setMedios).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cita.id_cita]);

  const subir = async (file) => {
    setSubiendo(true);
    try {
      const res = await addMedio(cita.id_cita, { tipo, notaContexto: nota, file });
      setMedios(res.medios || []);
      setNota("");
      onChanged();
      toast.success("Medio agregado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setSubiendo(false);
    }
    return false; // evita que antd Upload intente subirlo solo
  };

  const eliminar = async (idMedio) => {
    try {
      const res = await deleteMedio(cita.id_cita, idMedio);
      setMedios(res.medios || []);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar el medio");
    }
  };

  return (
    <div>
      <List
        grid={{ gutter: 12, column: 3 }}
        dataSource={medios}
        locale={{ emptyText: <Empty description="Sin fotos ni videos" /> }}
        renderItem={(m) => (
          <List.Item>
            <div className="agc-medio-card">
              {m.tipo === "foto" ? (
                <img src={`${PATH}/${m.path}`} alt={m.filename} />
              ) : (
                <video src={`${PATH}/${m.path}`} controls />
              )}
              <div className="agc-medio-info">
                <span>{m.nota_contexto || m.filename}</span>
                <Popconfirm title="¿Eliminar este medio?" onConfirm={() => eliminar(m.id_medio)}>
                  <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
          </List.Item>
        )}
      />
      <Divider />
      <Space wrap>
        <Select value={tipo} onChange={setTipo} style={{ width: 120 }} options={[{ value: "foto", label: "Foto" }, { value: "video", label: "Video" }]} />
        <Input placeholder="Nota de contexto (opcional)" style={{ width: 220 }} value={nota} onChange={(e) => setNota(e.target.value)} />
        <Upload beforeUpload={subir} showUploadList={false} accept={tipo === "foto" ? "image/*" : "video/*"}>
          <Button icon={<UploadOutlined />} loading={subiendo}>Subir {tipo}</Button>
        </Upload>
      </Space>
    </div>
  );
}

// ---------------- tab: consentimientos ----------------

function TabConsentimientos({ cita, onChanged }) {
  const [consentimientos, setConsentimientos] = useState(cita.consentimientos || []);
  const [tipos, setTipos] = useState([]);
  const [idTipo, setIdTipo] = useState(null);
  const [version, setVersion] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listConsentimientos(cita.id_cita).then(setConsentimientos).catch(() => {});
    listTiposConsentimiento().then(setTipos).catch(() => setTipos([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cita.id_cita]);

  const crear = async () => {
    if (!idTipo || !version.trim()) {
      toast.error("Selecciona el tipo y la versión del documento");
      return;
    }
    setLoading(true);
    try {
      const res = await createConsentimiento(cita.id_cita, { id_tipo_consentimiento: idTipo, version_documento: version.trim() });
      setConsentimientos(res.consentimientos || []);
      setVersion("");
      setIdTipo(null);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar el consentimiento");
    } finally {
      setLoading(false);
    }
  };

  const firmar = async (idConsentimiento, firmadoPor) => {
    if (!firmadoPor || !firmadoPor.trim()) {
      toast.error("Indica quién firma");
      return;
    }
    try {
      const res = await firmarConsentimiento(cita.id_cita, idConsentimiento, firmadoPor.trim());
      setConsentimientos(res.consentimientos || []);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo firmar el consentimiento");
    }
  };

  return (
    <div>
      <List
        dataSource={consentimientos}
        locale={{ emptyText: <Empty description="Sin consentimientos" /> }}
        renderItem={(c) => (
          <ConsentimientoItem c={c} onFirmar={firmar} />
        )}
      />
      <Divider />
      <Space wrap>
        <Select
          style={{ width: 220 }}
          placeholder="Tipo de consentimiento"
          value={idTipo}
          onChange={setIdTipo}
          options={tipos.map((t) => ({ value: t.id_tipo_consentimiento, label: t.nombre }))}
        />
        <Input placeholder="Versión del documento" style={{ width: 160 }} value={version} onChange={(e) => setVersion(e.target.value)} />
        <Button type="primary" icon={<PlusOutlined />} loading={loading} onClick={crear}>Agregar</Button>
      </Space>
    </div>
  );
}

function ConsentimientoItem({ c, onFirmar }) {
  const [firmadoPor, setFirmadoPor] = useState("");
  const firmado = c.estado === "firmado";

  return (
    <List.Item
      actions={
        firmado
          ? []
          : [
              <Space>
                <Input
                  size="small"
                  placeholder="Firmado por"
                  style={{ width: 140 }}
                  value={firmadoPor}
                  onChange={(e) => setFirmadoPor(e.target.value)}
                />
                <Button size="small" type="primary" onClick={() => onFirmar(c.id_consentimiento, firmadoPor)}>
                  Marcar firmado
                </Button>
              </Space>,
            ]
      }
    >
      <span>
        {c.tipo_consentimiento_nombre} — v{c.version_documento}{" "}
        <Tag color={firmado ? "green" : "gold"}>{firmado ? "Firmado" : "Pendiente"}</Tag>
        {firmado && c.firmado_por ? ` por ${c.firmado_por}` : ""}
      </span>
    </List.Item>
  );
}
